class CustomReviewCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  track = null;
  /** @type {Element[]} */
  dots = [];
  /** @type {boolean} */
  autoplay = false;
  /** @type {number} */
  autoplaySpeed = 4000;
  /** @type {AbortController | null} */
  abortController = null;
  /** @type {number | undefined} */
  autoplayTimer = undefined;
  connectedCallback() {
    this.track = this.querySelector('[data-review-track]');
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = Number(this.dataset.autoplaySpeed) || 4000;

    if (!this.track) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.track.addEventListener('scroll', this.handleScroll, { signal, passive: true });
    this.track.addEventListener('pointerdown', this.handlePointerDown, { signal });
    this.addEventListener('pointerenter', this.stopAutoplay, { signal });
    this.addEventListener('pointerleave', this.startAutoplay, { signal });
    this.addEventListener('focusin', this.stopAutoplay, { signal });
    this.addEventListener('focusout', this.startAutoplay, { signal });

    this.resizeObserver = new ResizeObserver(() => {
      this.setupDots();
    });
    this.resizeObserver.observe(this.track);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.abortController?.abort();
    this.stopAutoplay();
  }

  setupDots = () => {
    const container = this.querySelector('[data-review-dots-container]');
    if (!(container instanceof HTMLElement) || !this.track) return;

    const pageCount = Math.round(this.track.scrollWidth / this.track.clientWidth) || 1;

    // Clear existing dots
    container.innerHTML = '';
    this.dots = [];

    if (pageCount <= 1) {
      container.style.display = 'none';
      this.stopAutoplay();
      return;
    }

    container.style.display = 'flex';

    for (let i = 0; i < pageCount; i++) {
      const button = document.createElement('button');
      button.className = 'custom-review-carousel__dot';
      button.type = 'button';
      button.dataset.reviewDot = i.toString();
      button.setAttribute('aria-label', `Show review page ${i + 1}`);

      const { signal } = this.abortController || {};
      button.addEventListener('click', this.handleDotClick, { signal });

      container.appendChild(button);
      this.dots.push(button);
    }

    this.updateDots();
    this.startAutoplay();
  };

  handleScroll = () => {
    window.requestAnimationFrame(() => this.updateDots());
  };

  /**
   * @param {MouseEvent} event
   */
  handleDotClick = (event) => {
    if (!(event.currentTarget instanceof HTMLElement)) return;
    const index = Number(event.currentTarget.dataset.reviewDot) || 0;
    this.stopAutoplay();
    this.scrollToPage(index);
    this.startAutoplay();
  };

  /**
   * @param {PointerEvent} event
   */
  handlePointerDown = (event) => {
    const track = this.track;
    if (!track) return;
    if (event.button !== 0 || !(event.target instanceof Element) || event.target.closest('button, a, input, textarea, select')) return;
    if (track.scrollWidth <= track.clientWidth) return;

    this.stopAutoplay();
    this.setAttribute('dragging', '');

    const startX = event.clientX;
    const startScrollLeft = track.scrollLeft;
    let hasMoved = false;

    /**
     * @param {PointerEvent} moveEvent
     */
    const handlePointerMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      if (Math.abs(delta) > 3) hasMoved = true;
      track.scrollLeft = startScrollLeft - delta;
    };

    const handlePointerUp = () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
      this.removeAttribute('dragging');
      if (hasMoved) this.snapToNearestPage();
      this.startAutoplay();
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
  };

  module = null;

  startAutoplay = () => {
    if (!this.autoplay || this.dots.length === 0 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stopAutoplay();
    this.autoplayTimer = window.setInterval(() => this.goToNextPage(), this.autoplaySpeed);
  };

  stopAutoplay = () => {
    window.clearInterval(this.autoplayTimer);
  };

  goToNextPage() {
    const currentPage = this.getCurrentPage();
    const nextPage = currentPage >= this.dots.length - 1 ? 0 : currentPage + 1;
    this.scrollToPage(nextPage);
  }

  snapToNearestPage() {
    this.scrollToPage(this.getCurrentPage());
  }

  /**
   * @param {number} index
   */
  scrollToPage(index) {
    if (!this.track) return;
    this.track.scrollTo({
      left: index * this.track.clientWidth,
      behavior: 'smooth',
    });
  }

  getCurrentPage() {
    if (!this.track || !this.track.clientWidth) return 0;
    const page = Math.round(this.track.scrollLeft / this.track.clientWidth);
    return Math.max(0, Math.min(page, Math.max(0, this.dots.length - 1)));
  }

  updateDots() {
    if (this.dots.length === 0) return;
    const currentPage = this.getCurrentPage();

    this.dots.forEach((dot, index) => {
      const isActive = index === currentPage;
      dot.classList.toggle('custom-review-carousel__dot--active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('custom-review-carousel')) {
  customElements.define('custom-review-carousel', CustomReviewCarousel);
}
